import { find, get, some, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslate } from '../../../../core/translate';
import FeatureFilter, {
  FeatureFilterRadio,
  FeatureFilterSection,
} from '../../../common/featureFilter/FeatureFilter';
import { withNotifications } from '../../../common/notifications/NotificationsContext';
import getDriveTypeDescription from '../../../utils/getDriveTypeDescription';
import { buildDriveOptions, getFilteredOptions } from '../utils';

export const driveTypeSectionEl = (
  isInHeader = null,
  selectedDriveType,
  driveOptions,
  onDriveSelect,
  translate,
  driveTypeAnalyticsKey
) => {
  return (
    selectedDriveType && (
      <FeatureFilterSection
        onChange={onDriveSelect}
        title={translate('component.common.select.drive.type')}
        modifier={`drive-type${isInHeader ? '-header' : ''}`}>
        {driveOptions.map(({ driveType, rearWheelType, id, label }) => (
          <FeatureFilterRadio
            key={id}
            name="driveoptions"
            value={id}
            checked={
              selectedDriveType.driveType === driveType &&
              selectedDriveType.rearWheelType === rearWheelType
            }
            primaryDescription={label}
            secondaryDescription={
              rearWheelType
                ? rearWheelType
                : getDriveTypeDescription(driveType, translate)
            }
            analyticsKey={driveTypeAnalyticsKey}></FeatureFilterRadio>
        ))}
      </FeatureFilterSection>
    )
  );
};

export const getConflictNotificationMessage = (
  conflictNotification,
  selectedDriveType,
  selectedOptionDescription,
  optionLabel,
  translate
) => {
  const { display, basedOn } = conflictNotification;
  if (display) {
    let category = '';
    let resolvedSelection = '';
    let userSelection = '';

    switch (basedOn) {
      case 'driveType':
        resolvedSelection = selectedOptionDescription;
        userSelection = selectedDriveType.rearWheelType
          ? `${selectedDriveType.driveType} ${selectedDriveType.rearWheelType}`
          : selectedDriveType.driveType;
        category = optionLabel;
        break;
      case 'option':
        resolvedSelection =
          selectedDriveType.rearWheelType &&
          selectedDriveType.rearWheelType !== ''
            ? `${selectedDriveType.driveType} ${selectedDriveType.rearWheelType}`
            : selectedDriveType.driveType;

        userSelection = selectedOptionDescription;
        category = translate('component.common.select.drive.type');
        break;
      default:
        break;
    }
    return translate('feature.compare.conflict.v3', {
      category,
      resolvedSelection,
      userSelection,
    });
  }
  return null;
};

const OptionsFilter = ({
  selections,
  options,
  config,
  children,
  analyticsKey,
  notifications,
}) => {
  const [selectedDriveType, setSelectedDriveType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const { show } = notifications;
  const translate = useTranslate();

  const [conflictNotification, setConflictNotification] = useState({
    display: false,
    basedOn: '',
  });

  const normalizedOptions = options.map((option) => {
    const id = +option.bodyTypeId;
    const bodyType = find(config.bodyTypes, { id });

    return {
      ...option,
      rearWheelType: get(bodyType, 'rearWheelType', ''),
      hasWheelBase: get(bodyType, 'wheelBaseType', ''),
    };
  });

  const driveOptions = buildDriveOptions(normalizedOptions, config.driveTypes);
  const uniqueBodyTypes = uniqBy(options, 'bodyTypeName');

  useEffect(() => {
    const selectedDT = find(driveOptions, {
      rearWheelType: selections.rearWheelType || null,
      driveType: selections.driveType,
    });

    const hasCabSizes = some(uniqueBodyTypes, 'cabSize');
    const selectedOpt = hasCabSizes
      ? find(uniqueBodyTypes, { cabSize: selections.cabSize })
      : find(uniqueBodyTypes, { bodyTypeId: `${selections.bodyTypeId}` });

    setSelectedDriveType(selectedDT);
    setSelectedOption(selectedOpt);
    setFilteredOptions(
      getFilteredOptions(normalizedOptions, selectedDT, selectedOpt)
    );
  }, [selections]);

  useEffect(() => {
    if (conflictNotification.display) {
      show(conflictNotificationMessage);
    }
  }, [conflictNotification]);

  const onDriveSelect = (e) => {
    let driveTypeValue = find(driveOptions, { id: e.target.value });
    const proposedOptions = getFilteredOptions(
      normalizedOptions,
      driveTypeValue,
      selectedOption
    );
    setSelectedDriveType(driveTypeValue);
    if (proposedOptions.length > 0) {
      setFilteredOptions(proposedOptions);
      setConflictNotification({ display: false, basedOn: '' });
    } else {
      const newOption = getFilteredOptions(
        normalizedOptions,
        driveTypeValue,
        null
      )[0];
      // TODO: We can adjust logic for finding closest option to previous selection if we want to as well on conflict here
      // go by previous cab size / body type / etc if we want to
      const newSelection = find(uniqueBodyTypes, {
        bodyTypeName: newOption.bodyTypeName,
      });
      setSelectedOption(newSelection);
      // TODO: Confirm its better here to grab trims over just selecting first available filtered option?
      // Just felt like it is safer to get final filtered value here.
      setFilteredOptions(
        getFilteredOptions(normalizedOptions, driveTypeValue, newSelection)
      );
      setConflictNotification({ display: true, basedOn: 'driveType' });
    }
  };

  const onSelectOption = (e) => {
    const newOption = find(uniqueBodyTypes, { bodyTypeName: e.target.value });
    const proposedOptions = getFilteredOptions(
      normalizedOptions,
      selectedDriveType,
      newOption
    );

    setSelectedOption(newOption);
    if (proposedOptions.length > 0) {
      setFilteredOptions(proposedOptions);
      setConflictNotification({ display: false, basedOn: '' });
    } else {
      const newTrim = getFilteredOptions(normalizedOptions, null, newOption)[0];

      // TODO: We can adjust logic for finding closest option to previous selection if we want to as well on conflict here.
      let newDriveType = find(driveOptions, {
        rearWheelType: newTrim.rearWheelType || null,
        driveType: newTrim.driveType,
      });
      setSelectedDriveType(newDriveType);
      setFilteredOptions(
        getFilteredOptions(normalizedOptions, newDriveType, newOption, config)
      );
      setConflictNotification({ display: true, basedOn: 'option' });
    }
  };

  const showOptionSelect = uniqueBodyTypes && uniqueBodyTypes.length > 1;
  const showDriveTypeSelect = driveOptions && driveOptions.length > 1;
  const displayFilterCount = showOptionSelect || showDriveTypeSelect;
  const displayInTableFilter =
    uniqueBodyTypes && uniqueBodyTypes.length < 2 && showDriveTypeSelect;

  const getCompareFilterPills = () => {
    let pillsList = [];
    if (selectedOption && showOptionSelect) {
      pillsList = [...pillsList, `${selectedOption.bodyTypeName}`];
    }
    if (selectedDriveType && showDriveTypeSelect) {
      let driveTypeText = `${selectedDriveType.label}`;
      if (selectedDriveType.rearWheelType) {
        driveTypeText = `${driveTypeText} ${selectedDriveType.rearWheelType}`;
      }
      pillsList = [...pillsList, driveTypeText];
    }

    return pillsList;
  };

  const cabBoxLabel = translate('feature.compare.cab.style.v3');

  const getOptionLabel = () => {
    if (some(normalizedOptions, 'cabSize')) {
      return cabBoxLabel;
    }

    if (some(normalizedOptions, 'hasWheelBase')) {
      return translate('feature.compare.wheelbase.v3');
    }

    return translate('feature.compare.body.style.v3');
  };

  const optionLabel = getOptionLabel();
  const pillsList = getCompareFilterPills();

  const conflictNotificationMessage = getConflictNotificationMessage(
    conflictNotification,
    selectedDriveType,
    selectedOption?.bodyTypeName,
    optionLabel,
    translate
  );

  const driveTypeAnalyticsKey = analyticsKey + ':filters:drive type';
  const optionAnalyticsKey =
    analyticsKey +
    ':filters:' +
    (optionLabel === cabBoxLabel
      ? translate('feature.compare.cab.analytics.v3')
      : optionLabel);
  const driveTypeSection = driveTypeSectionEl(
    false,
    selectedDriveType,
    driveOptions,
    onDriveSelect,
    translate,
    driveTypeAnalyticsKey
  );
  const driveTypeSectionInHeader = driveTypeSectionEl(
    true,
    selectedDriveType,
    driveOptions,
    onDriveSelect,
    translate,
    driveTypeAnalyticsKey
  );

  return (
    <>
      {showOptionSelect && selectedOption && selectedDriveType && (
        <FeatureFilter pillsList={pillsList} analyticsKey={analyticsKey}>
          <div className="compare-filter__container">
            <FeatureFilterSection
              onChange={onSelectOption}
              modifier="body-type"
              title={optionLabel}>
              {uniqueBodyTypes.map(({ bodyTypeName }) => (
                <FeatureFilterRadio
                  name="bodytypes"
                  key={bodyTypeName}
                  value={bodyTypeName}
                  checked={bodyTypeName === selectedOption.bodyTypeName}
                  primaryDescription={bodyTypeName}
                  analyticsKey={optionAnalyticsKey}></FeatureFilterRadio>
              ))}
            </FeatureFilterSection>
            {showDriveTypeSelect && driveTypeSection}
          </div>
        </FeatureFilter>
      )}
      {children(
        filteredOptions,
        displayFilterCount,
        displayInTableFilter,
        driveTypeSectionInHeader
      )}
    </>
  );
};

export default withNotifications(OptionsFilter);
